import { DEFAULT_FILTER_IDS, GetCampaignPerformanceDashboardArgs, PerformanceAudienceFilterOption } from '@/services/endpoints/performanceAudience';
import { PerformanceAudienceFiltersState } from '../types';
import { resolveFiltersDateRange } from './resolveDateRangePreset';

const ALL_SCOPE_IDS = new Set(['google-all', 'meta-all']);

export const isAllAccountsScopeId = (scopeId: string): boolean => ALL_SCOPE_IDS.has(scopeId) || scopeId.endsWith('-all');

export type BuildCampaignPerformanceDashboardArgsOptions = {
  audiences?: PerformanceAudienceFilterOption[];
  googleScopes?: PerformanceAudienceFilterOption[];
  metaScopes?: PerformanceAudienceFilterOption[];
};

export const getRealAccountOptions = (options: PerformanceAudienceFilterOption[]): PerformanceAudienceFilterOption[] =>
  options.filter((option) => option.id && !isAllAccountsScopeId(option.id));

export const expandAccountIds = (selectedId: string, options: PerformanceAudienceFilterOption[]): string => {
  if (!options.length) {
    return '';
  }

  if (!selectedId) {
    return getRealAccountOptions(options)
      .map((option) => option.id)
      .join(',');
  }

  if (!isAllAccountsScopeId(selectedId)) {
    const exists = options.some((option) => option.id === selectedId);
    return exists ? selectedId : '';
  }

  return getRealAccountOptions(options)
    .map((option) => option.id)
    .join(',');
};

export const expandSelectedAccountIds = (selectedIds: string[], options: PerformanceAudienceFilterOption[]): string => {
  if (!options.length || !selectedIds.length) {
    return '';
  }

  const realIds = new Set(getRealAccountOptions(options).map((option) => option.id));
  return selectedIds.filter((id) => realIds.has(id) && !isAllAccountsScopeId(id)).join(',');
};

export const resolveAudienceIdForDashboard = (selectedAudienceId: string, audiences: PerformanceAudienceFilterOption[] = []): string => {
  if (!selectedAudienceId) {
    return '';
  }

  const selectedAudience = audiences.find((option) => option.id === selectedAudienceId);
  const externalId = selectedAudience?.externalId?.trim();

  return externalId || '';
};

export const canBuildDashboardArgs = (filters: PerformanceAudienceFiltersState, options: BuildCampaignPerformanceDashboardArgsOptions = {}): boolean => {
  if (!resolveAudienceIdForDashboard(filters.audienceId, options.audiences)) {
    return false;
  }

  if (filters.dateRange === 'custom' && (!filters.dateFrom || !filters.dateTo)) {
    return false;
  }

  const googleAccountIds = expandAccountIds(filters.googleScopeId, options.googleScopes ?? []);
  const metaAccountIds = expandSelectedAccountIds(filters.metaScopeIds ?? [], options.metaScopes ?? []);

  return Boolean(googleAccountIds || metaAccountIds);
};

export const parseAccountCountFromSuffix = (suffix?: string): number | undefined => {
  if (!suffix) {
    return undefined;
  }

  const match = suffix.match(/(\d+)\s+(?:ad\s+)?account/i);
  return match ? Number(match[1]) : undefined;
};

export const buildCampaignPerformanceDashboardArgs = (
  workspaceId: string,
  filters: PerformanceAudienceFiltersState,
  options: BuildCampaignPerformanceDashboardArgsOptions = {}
): GetCampaignPerformanceDashboardArgs => {
  const { from, to } = resolveFiltersDateRange({
    dateRange: filters.dateRange ?? DEFAULT_FILTER_IDS.dateRange,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });

  return {
    workspaceId,
    audience_id: resolveAudienceIdForDashboard(filters.audienceId, options.audiences),
    from,
    to,
    platform: 'meta',
    google_account_ids: expandAccountIds(filters.googleScopeId, options.googleScopes ?? []),
    meta_account_ids: expandSelectedAccountIds(filters.metaScopeIds ?? [], options.metaScopes ?? []),
  };
};
